import React from 'react';
import clsx from 'clsx';
import Button from 'components/UI/V3/Button/Button';
import { Switch } from 'components/UI/V3/Forms/Switch/Switch';
import { ValueTypeSelector } from 'components/UI/V3/Forms/ValueTypeSelector';
import ExpandableSelector from 'components/UI/V3/ExpandableSelector';
import { DatePicker } from 'components/UI/V3/DatePicker/DatePicker';
import { TimeSelector } from 'components/UI/V3/Forms/TimeSelector/TimeSelector';
import Checkbox from 'components/UI/V3/Forms/Checkbox';

import { usePromoCodeDetailsStep } from './hooks/usePromoCodeDetailsStep';
import { PromoCodeNameField } from './PromoCodeNameField';
import styles from './PromoCodeDetailsStep.module.scss';
import { DiscountType, SavePromocode } from '@spike/promocode-model';

interface PromoCodeDetailsStepProps {
    promoCode: SavePromocode;
    onNext: (formData: Partial<SavePromocode>) => void;
    isSaving?: boolean;
    lockConfiguration?: boolean;
}

export const PromoCodeDetailsStep: React.FC<PromoCodeDetailsStepProps> = props => {
    const {
        formState,
        expandedSelectors,
        isValid,
        startTimeOptions,
        endTimeOptions,
        today,
        timeZone,
        handlers,
        getFormData
    } = usePromoCodeDetailsStep(props.promoCode, props.lockConfiguration);

    const nextHandler = React.useCallback(() => {
        props.onNext(getFormData());
    }, [props.onNext, getFormData]);

    return (
        <div className={styles.root}>
            <PromoCodeNameField
                value={formState.name.value}
                error={formState.name.error}
                disabled={props.lockConfiguration}
                onChange={handlers.handlePromoCodeNameChange}
            />
            <ValueTypeSelector
                label="Value"
                required={true}
                className={styles.valueTypeSelector}
                disabled={props.lockConfiguration}
                onChange={handlers.handleValueChange}
                value={{
                    value: formState.value.value,
                    type: formState.value.type === DiscountType.FIXED ? 'amount' : 'percentage'
                }}
                error={formState.value.error}
            />
            <div className={clsx(styles.dateSelector, { [styles.locked]: props.lockConfiguration })}>
                <ExpandableSelector
                    title="Start Date"
                    id="start-date-selector"
                    expanded={expandedSelectors.startAt}
                    childrenClassName={styles.dateSelector_children}
                    className={styles.date}
                    label={formState.startAt.format('MMM, DD YYYY')}
                    disabled={props.lockConfiguration}
                    onExpand={() => handlers.handleExpandSelector('startAt')}
                    onClickAway={() => handlers.handleClickAwaySelector('startAt')}
                >
                    <DatePicker
                        minDate={today}
                        maxDate={today.clone().add(1, 'year')}
                        timeZone={timeZone}
                        value={formState.startAt}
                        onChange={handlers.createDateChangeHandler('start')}
                    />
                </ExpandableSelector>

                <div className={styles.centeredTimeSelector}>
                    <TimeSelector
                        label="Start Time"
                        value={startTimeOptions.find(option => option.id === formState.startAt.format('hh:mma'))}
                        options={startTimeOptions}
                        disabled={props.lockConfiguration}
                        onChange={handlers.createTimeChangeHandler('start')}
                    />
                </div>
            </div>

            <Checkbox
                label="Set End Date"
                checked={formState.endAtEnabled}
                onChange={handlers.toggleEndAtEnabled}
                className={clsx(styles.checkBox, { [styles.locked]: props.lockConfiguration })}
            />

            {formState.endAtEnabled && (
                <div className={clsx(styles.dateSelector, { [styles.locked]: props.lockConfiguration })}>
                    <ExpandableSelector
                        title="End Date"
                        id="end-date-selector"
                        expanded={expandedSelectors.endAt}
                        childrenClassName={styles.dateSelector_children}
                        label={formState.endAt.format('MMM, DD YYYY')}
                        disabled={props.lockConfiguration}
                        onExpand={() => handlers.handleExpandSelector('endAt')}
                        onClickAway={handlers.handleEndAtClickAway}
                    >
                        <DatePicker
                            minDate={today}
                            maxDate={today.clone().add(1, 'year')}
                            timeZone={timeZone}
                            value={formState.endAt}
                            onChange={handlers.createDateChangeHandler('end')}
                            className={styles.datePicker}
                        />
                    </ExpandableSelector>

                    <TimeSelector
                        label="End Time"
                        value={endTimeOptions.find(option => option.id === formState.endAt.format('hh:mma'))}
                        options={endTimeOptions}
                        disabled={props.lockConfiguration}
                        onChange={handlers.createTimeChangeHandler('end')}
                    />
                </div>
            )}

            <Switch
                label="Online Booking"
                inlineLabel="Redeem online"
                className={styles.redeemSwitch}
                checked={formState.olb}
                onChange={handlers.handleOlbChange}
            />

            <Button
                label={props.isSaving ? 'Saving...' : 'Save'}
                size="large"
                noColorOnDisabled
                disabled={!isValid || props.isSaving}
                className={styles.submitButton}
                onClick={nextHandler}
            />
        </div>
    );
};
